/**
 * Is the game connected? One answer for the whole game, from evidence, never from a clock alone:
 *
 *   connected   the last thing we heard from our servers came back
 *   connecting  nothing heard yet (starting up)
 *   degraded    something failed at the network level, but our servers still answer when asked (a slow or patchy
 *               connection: the game carries on, quietly retrying)
 *   offline     requests fail at the network level AND both our servers fail a direct check (twice, uncached), or the
 *               browser says it's offline and a check agrees: the network-error screen
 *   server-error our servers are reached but answer 5xx when checked: the server's problem, not the connection
 *
 * Evidence comes in from every fetch the game makes (a fetch that throws is a network failure; a response, any status,
 * proves the network works), from the realtime sockets (one that closes without ever opening), and from the browser's
 * online/offline events. A slow download is never evidence: it hasn't failed. While offline, the check runs again (more
 * slowly each time, and at once on the 'online' event); the moment it passes, everything waiting on the connection is
 * told (onRecover) and the screen goes, no reload.
 */
export type Health = 'connected' | 'connecting' | 'degraded' | 'offline' | 'server-error';

const API = (): string | null => {
  const q = new URLSearchParams(location.search).get('mp');
  const u = q === 'off' ? null : q ?? (import.meta.env.VITE_MP_URL as string | undefined) ?? (import.meta.env.DEV ? 'ws://localhost:8787' : null);
  return u ? u.replace(/^ws/, 'http').replace(/\/$/, '') : null;
};

/** one check of one server: 'ok', 'down' (the request itself failed: no network to it), or 'error' (it answered 5xx) */
async function check(url: string): Promise<'ok' | 'down' | 'error'> {
  const c = new AbortController(), t = setTimeout(() => c.abort(), 8000); // (a check that hangs counts as failed: it's the check, not the game's loading)
  try { const r = await realFetch(url, { cache: 'no-store', signal: c.signal }); return r.status >= 500 ? 'error' : 'ok'; } catch { return 'down'; } finally { clearTimeout(t); }
}
const realFetch: typeof fetch = window.fetch.bind(window);

class ConnectionHealth {
  state: Health = 'connecting';
  private fails: number[] = [];
  private checking: Promise<void> | null = null;
  private retryIn = 0;
  private retryT: ReturnType<typeof setTimeout> | null = null;
  private listeners = new Set<(h: Health, was: Health) => void>();
  private recover = new Set<() => void>();
  /** the state changed */
  on(f: (h: Health, was: Health) => void) { this.listeners.add(f); return () => this.listeners.delete(f); }
  /** back after a network error (or a server error): retry what was waiting */
  onRecover(f: () => void) { this.recover.add(f); return () => this.recover.delete(f); }
  private set(h: Health) {
    if (h === this.state) return;
    const was = this.state;
    this.state = h;
    for (const f of this.listeners) { try { f(h, was); } catch { /* a listener's own problem */ } }
    if ((was === 'offline' || was === 'server-error') && (h === 'connected' || h === 'degraded')) for (const f of this.recover) { try { f(); } catch { /* ditto */ } }
  }
  /** something reached a server (any answer: the network works) */
  ok() {
    this.fails = [];
    if (this.state === 'connecting' || this.state === 'degraded') this.set('connected');
    else if (this.state === 'offline' || this.state === 'server-error') void this.confirm(); // (one answer isn't enough to call it back: check)
  }
  /** a request failed at the network level (it threw; not a status): evidence, checked before anything's shown */
  fail() {
    const now = Date.now();
    this.fails = this.fails.filter((t) => now - t < 30_000);
    this.fails.push(now);
    if (this.state === 'offline' || this.state === 'server-error') return;
    void this.confirm();
  }
  /** a server answered 5xx */
  serverFail() { if (this.state !== 'offline') void this.confirm(); }
  /** ask both servers directly, twice; decide from what they say */
  confirm(): Promise<void> {
    if (this.checking) return this.checking;
    this.checking = (async () => {
      const api = API(), urls = [`${location.origin}/logo.png?probe=${Date.now()}`, ...(api ? [`${api}/health?probe=${Date.now()}`] : [])];
      let res = await Promise.all(urls.map(check));
      if (res.some((r) => r !== 'ok')) { await new Promise((r) => setTimeout(r, 1500)); res = await Promise.all(urls.map((u) => check(u.replace(/probe=\d+/, `probe=${Date.now()}`)))); }
      if (res.every((r) => r === 'down')) this.set('offline');
      else if (res.includes('error')) this.set('server-error');
      else if (res.includes('down')) this.set('degraded'); // (one answers: it's patchy, or one server; not "no internet")
      else this.set(this.fails.length && Date.now() - this.fails[this.fails.length - 1] < 10_000 ? 'degraded' : 'connected');
      this.checking = null;
      this.schedule();
    })();
    return this.checking;
  }
  /** while it's down: check again, every 3 s at first, then less often (at most every 20 s) */
  private schedule() {
    if (this.retryT) { clearTimeout(this.retryT); this.retryT = null; }
    if (this.state !== 'offline' && this.state !== 'server-error') { this.retryIn = 0; return; }
    this.retryIn = Math.min(20_000, this.retryIn ? this.retryIn * 1.6 : 3000);
    this.retryT = setTimeout(() => void this.confirm(), this.retryIn);
  }
}

export const health = new ConnectionHealth();

let installed = false;
/**
 * Watch every request the game makes (ours and our server's only: a third party failing isn't our connection), the
 * browser's online/offline, and give the realtime sockets a way to report.
 */
export function watchConnection() {
  if (installed) return;
  installed = true;
  const api = API();
  const ours = (u: string) => u.startsWith('/') || u.startsWith(location.origin) || (!!api && u.startsWith(api));
  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const p = realFetch(input, init);
    if (ours(url)) p.then((r) => { if (r.status >= 500 && api && url.startsWith(api)) health.serverFail(); else health.ok(); }, (e: unknown) => { if ((e as { name?: string })?.name !== 'AbortError') health.fail(); });
    return p;
  };
  addEventListener('offline', () => health.fail());
  addEventListener('online', () => void health.confirm());
}
/** a realtime socket: it opened (the network works), or it closed without ever opening (evidence) */
export function socketOpened() { health.ok(); }
export function socketFailed() { health.fail(); }

/**
 * The screens: offline, a card over the game ("No connection", reconnecting by itself, the game untouched behind it);
 * the server's own trouble, its own card. Gone the moment it's back.
 */
export function connectionScreens() {
  const css = document.createElement('style');
  css.textContent = `#conn { position:fixed; inset:0; z-index:2147482000; display:flex; align-items:center; justify-content:center; padding:16px; background:rgba(12,9,7,.86); font:14px/1.5 "Segoe UI", system-ui, sans-serif; color:#f3ead9; }
  #conn .n-card { max-width:340px; text-align:center; padding:26px 22px; border-radius:14px; background:#1c1612; border:1px solid rgba(243,234,217,.14); box-shadow:0 20px 60px rgba(0,0,0,.5); }
  #conn .n-ico { font-size:38px; margin-bottom:6px; } #conn b { display:block; font:600 20px Georgia, serif; margin-bottom:6px; } #conn p { margin:0 0 14px; color:#c9bba6; }
  #conn button { padding:10px 22px; border:0; border-radius:8px; background:#d2693a; color:#fff; font:600 15px "Segoe UI", sans-serif; cursor:pointer; } #conn button:disabled { opacity:.6; }
  #conn small { display:block; margin-top:10px; color:#e8a24a; min-height:1em; }`;
  document.head.appendChild(css);
  let card: HTMLDivElement | null = null;
  const show = (h: Health) => {
    if (h === 'offline' || h === 'server-error') {
      card?.remove();
      card = document.createElement('div'); card.id = 'conn'; card.dataset.state = h;
      card.innerHTML = h === 'offline'
        ? '<div class="n-card"><div class="n-ico">\u{1F4F6}</div><b>No connection</b><p>Unfold can’t reach the internet right now. Check your data or Wi-Fi. Your life is safe: the game picks up again by itself when you’re back.</p><button>Try now</button><small>Reconnecting…</small></div>'
        : '<div class="n-card"><div class="n-ico">⚠️</div><b>Our server isn’t answering</b><p>It’s on our side, not your connection. Your life is safe on this device; the game carries on by itself when the server’s back.</p><button>Try now</button><small>Checking again shortly…</small></div>';
      const b = card.querySelector('button')!, note = card.querySelector('small')!;
      b.onclick = () => { b.disabled = true; note.textContent = 'Checking…'; void health.confirm().then(() => { b.disabled = false; if (health.state === h) note.textContent = h === 'offline' ? 'Still no connection.' : 'Still not answering.'; }); };
      document.body.appendChild(card);
      return;
    }
    card?.remove(); card = null;
    // (degraded: nothing on screen. A patchy connection the servers still answer through is the game's normal weather:
    // what failed is retried; a pill for it would cry wolf)
  };
  health.on((h) => show(h));
}
